import { NestedValue } from 'react-hook-form';
import { CategoryFilterValues, CustomTranslation, FieldValuePair, GradientPickerValue, media as MediaInterface, PageData } from './interfaces';

export interface SpeakersComponentInterface extends UiComponent {
  sortBy: 'ASC' | 'DESC' | 'ALPHABETIC' | 'none';
  entryIds: SpeakerEntry[];
  customStyling: {
    showPositions: boolean;
    modalTextColor: string;
  };
}

export interface AgendaComponentInterface extends UiComponent {
  groupBy: AgendaGroupBy;
  entryList: AgendaEntry[];
  allowEntriesFavoriting: boolean;
  enableHoverEffect: boolean;
  allowCalendarExport: boolean;
  customStyling: {
    urlText: string;
    detailsText: string;
  };
}

export interface AppointmentComponentInterface extends UiComponent {
  hideEmptyRooms: boolean;
}

export interface BoothLogosComponentInterface extends UiComponent {
  showBoothLogo?: boolean;
  spotlight?: boolean;
}

export interface BoothsComponentInterface
  extends UiComponent<{
    gradient?: GradientPickerValue;
    width?: number;
    isFixedWidth?: boolean;
    textSize?: number;
    boothTextColor?: string;
    boothBackgroundColor?: string;
    showPlaceholder?: boolean;
  }> {
  showSearch?: boolean;
  showFilter?: boolean;
  sortByMeetingMinutes?: boolean;
  preselectedFilter?: CategoryFilterValues;
  advancedFilters?: CategoryFilterValues[];
  excludeFilters?: CategoryFilterValues[];
  showOnlyWithMeeting?: boolean;
  filterDescription?: string;
  spotlightBooths?: string[];
  useRandomSort?: boolean;
  showIsOnlineCheckbox?: boolean;
  showBoothLikes?: boolean;
  alignment?: 'start' | 'center' | 'end';
  highlightOnline?: boolean;
}

export interface ChallengeComponentInterface extends UiComponent {
  challenge?: string;
}
export interface CountdownComponentInterface extends UiComponent<{ countdownTextSize?: number; fullWidth?: boolean }> {
  customTime?: Date | null;
  alignment?: string;
  postaction: string;
  isInternalRoute?: boolean;
  selectedPage?: string;
  redirectLink?: string;
}

export interface CTAButtonComponentInterface
  extends UiComponent<{
    buttonBackgroundColor?: string;
    height: number;
    width: number;
    buttonBorderRadius: number;
    buttonFontSize: number;
    buttonBackgroundColorIsDefault: boolean;
  }> {
  appendToken?: boolean;
  buttonType?: 'link' | 'data-update';
  url?: string | null;
  newTab?: boolean;
  isExternal: boolean;
  eventpage: string; // #10151 deprecated
  languageUrls: { [language: string]: string };
  translations?: CustomTranslation[];
  dataUpdateFields: FieldValuePair[];
  dataUpdateAction?: 'nothing' | 'open_page' | 'open_url';
}

export interface EventHomeComponentInterface extends UiComponent<{ bannerColor?: string; titleColor?: string }> {
  emptyMessage?: string;
  showDefaultTexts?: boolean;
  hideSideTexts?: boolean;
  showPresentationTickets?: boolean;
}

// deprecated and moved to image component
export interface GalleryComponentInterface
  extends UiComponent<{
    height?: number;
    width?: number;
    aspectRatio?: {
      x?: number;
      y?: number;
    };
  }> {
  autoplay?: boolean;
  backgroundImage?: string | null;
  isMasonry?: boolean;
  images?: any[];
  controls: boolean;
}

export interface HeroMenuComponentInterface extends UiComponent<{ topSpace: boolean }> {
  livepages?: any[];
  preText?: string;
  postText?: string;
}

export interface TeamHeroMenuItem {
  text: string;
  url?: string;
  openInNewTab: boolean;
  sortIndex: number;
}
export interface TeamHeroMenuComponentInterface extends UiComponent<{ topSpace: boolean; menuBackgroundColor: string; ItemTextColor: string }> {
  items?: TeamHeroMenuItem[];
  preText?: string;
  postText?: string;
}
export interface HTMLComponentInterface extends UiComponent<{ richTextWidth?: number }> {
  content?: string | null;
  alignment?: string;
  translations?: CustomTranslation[];
  fallbackValues?: { variable: string; value: string }[];
}
export interface ImageComponentInterface
  extends UiComponent<{
    height?: number;
    width?: number;
    aspectRatio?: {
      x?: number;
      y?: number;
    };
    alignment?: 'center' | 'left' | 'right';
  }> {
  imageFile?: string;
  imageLink?: string;
  imageType: 'file' | 'link';
  autoplay?: boolean;
  images?: any[];
  controls: boolean;
}
export interface InfoBoothComponentInterface extends UiComponent {
  boothId?: string;
}

export interface MarginComponentInterface extends UiComponent {
  margin?: number;
}

export interface MediaComponentInterface extends UiComponent<{ backgroundColor: string }> {
  showTitles?: boolean;
  media?: {
    mediaObject: MediaInterface;
    sortIndex: number;
    isDownloadable?: boolean;
  }[];
}

export interface NetworkComponentInterface
  extends UiComponent<{
    bannerBorderColor?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
    cardBackgroundColor?: string;
  }> {
  networks?: {
    networkid: string;
    sortIndex: number;
    visible: boolean;
  }[];
  sortType?: string;
  sortOrder?: string;
  showCount?: boolean;
}

export interface PhotoBoothComponentInterface extends UiComponent {
  title?: string;
  description?: string;
}

export interface PresentationComponentInterface extends UiComponent<{ presentationTextColor?: string }> {
  content?: string | null;
  template?: string;
  showPresentationStatus?: boolean;
  showDate?: boolean;
  showTime?: boolean;
  spotlightPresentations: string[];
  selectedPresentations: string[];
  groupByStartTime?: boolean;
  showStartEndTime?: boolean;
  hidePastPresentations?: boolean;
}

export interface PresentationPreviewComponentInterface extends UiComponent {
  showTime?: boolean;
}

export interface QueueComponentInterface extends UiComponent {
  /**
   * only to pass the eslint pre-commit tests,
   * once you have properties to add, delete this
   */
  [key: string]: any;
}

export interface SignupFormComponentInterface
  extends UiComponent<{
    fieldColors?: string;
    fieldsTextColor?: string;
    fieldsBorderColor?: string;
    selectFieldSelectedColor?: string;
    buttonColor?: string;
    buttonTextColor?: string;
    buttonWidth?: number;
    btnFontSize?: number;
  }> {
  labelText?: string;
  showFormHeader?: boolean;
  showUserRegisteredInfo?: boolean;
  userRegisteredInfo?: string;
  showFormHeaderEventDate?: boolean;
  showFormHeaderEventTime?: boolean;
  showFormHeaderRegion?: boolean;
  showFormHeaderHost?: boolean;
  showFormHeaderLogo?: boolean;
  showFormHeaderTitle?: boolean;
  showBackToEventButton?: boolean;
  redirectToSignupSuccess?: boolean;
  redirectToOrderOverview?: boolean;
  multipleTickets?: boolean;
  redirectToUrl?: boolean;
  redirectUrl?: string;
  newTabOnSuccess?: boolean;
  signupButtonText?: string;
  altSignupId?: string;
  showTicketGate?: boolean;
  dataPrivacySentence?: string;
  showDefaultDataProtectionNotice?: boolean;
  forceNewMail?: boolean;
  showEmailConfirmation?: boolean;
  hideGeneratedEmail?: boolean;
}

export interface SpeedDatingComponentInterface extends UiComponent<{ buttonBackgroundColor?: string; buttonTextColor?: string }> {
  name?: string;
  duration?: number;
  disableScore?: boolean;
  disableHistory?: boolean;
  startButtonText?: string;
}

export interface AllMatchesComponentInterface extends UiComponent {
  minimumScore?: number;
  disableScoreWhenZero?: boolean;
  title?: string;
  subtitle?: string;
}

export interface SocialMediaComponentInterface
  extends UiComponent<{
    socialMedia?: {
      iconBackgroundColor: string;
      isDefaultBackground?: boolean;
      borderRadius?: number;
      iconSize?: number;
    };
  }> {
  socialMedias?: string[];
  socialMediaLinks?: socialMediasType;
}

export interface MBWStageDownload {
  _id?: string;
  file: string | null;
  thumbnail?: string | null;
  description?: string;
  title?: string;
  sortIndex: number;
}

export interface StageComponentInterface extends UiComponent<{ buttonColor?: string; buttonTextColor?: string; sidePanelBackground?: string }> {
  stageSettings?: PageData['stageSettings'];
  entryList?: AgendaEntry[]; // mbw
  agendaTitle?: string; // mbw
  entryIds?: SpeakerEntry[]; // mbw speakers (same naming as speakers component)
  speakersTitle?: string; // mbw
  downloadsTitle?: string;
  downloads?: MBWStageDownload[]; // mbw
}

export interface VideoComponentInterface extends UiComponent {
  videoSettings?: {
    videoFile?: string;
    videoFiles?: {
      file: string;
      filetype: string;
      path?: string;
      muxId?: string;
      playback_ids?: { policy: string; id: string }[];
    }[];
    videotype?: string;
    thumbnail?: string;
    autoplay?: boolean;
    muted?: boolean;
    url?: string;
  };
}

export interface UserProfileComponentInterface extends UiComponent<{ formBackground: string; formOpacity: number }> {
  /**
   * only to pass the eslint pre-commit tests,
   * once you have properties to add, delete this
   */
  [key: string]: any;
}

export interface QAComponentInterface extends UiComponent {
  label: string;
  delay: number;
  maxLength: number;
}

export interface PollUiComponentInterface extends QAComponentInterface {
  allowUnregistered?: boolean;
  customStyling: {
    backgroundColor?: string;
    textColor?: string;
  };
}

export interface EventsListUiComponentInterface extends UiComponent<{ buttonColor: string; buttonTextColor?: string | null }> {
  eventsList: string[];
  selectAllEvents: boolean;
  hidePastEvents: boolean;
  openBtnText?: string;
  showEventEndTime?: boolean;
}

export interface ColumnComponentInterface extends UiComponent {
  columns: number;
  forceRowOnMobile: boolean;
  components: {
    component: string;
    sortIndex: number;
    name: string;
  }[];
}

export type SupportedPagebuilderThemes = 'default' | 'mbw' | 'premium';

export interface UiComponent<T = {}> {
  _id: string;
  page: string;
  title?: string | null;
  titleTranslations?: CustomTranslation[];
  description?: string;
  isFullWidth?: boolean;
  isVisible: boolean;
  accessType: 'user' | 'visitor' | 'anyone';
  userApproval: ('all' | 'accepted' | 'rejected' | 'pending')[];
  isRestricted: boolean;
  registrationFields: { _id?: string; field: string; value: string }[];
  componentType?: string;
  componentIndex: number;
  columnComponent?: string;
  titleFontSize?: number;
  descriptionFontSize?: number;
  styling: {
    backgroundColor?: string;
    backgroundOpacity?: number;
    textColor?: string;
    customMaxWidth?: number;
    height: number;
    topPaddingPercentage: number;
    bottomPaddingPercentage: number;
  };
  customStyling: T;
  theme: SupportedPagebuilderThemes;
  imageComponentType?: 'slideshow' | 'image';
}

export interface DividerComponentInterface extends UiComponent {
  type: string;
  stroke: number;
  color: string;
}

export type ALL_COMPONENTS_INTERSECTION_TYPE = AgendaComponentInterface &
  AppointmentComponentInterface &
  BoothLogosComponentInterface &
  BoothsComponentInterface &
  ChallengeComponentInterface &
  CountdownComponentInterface &
  CTAButtonComponentInterface &
  EventHomeComponentInterface &
  GalleryComponentInterface & // deprecated and moved to image component
  HeroMenuComponentInterface &
  HTMLComponentInterface &
  InfoBoothComponentInterface &
  MarginComponentInterface &
  MediaComponentInterface &
  NetworkComponentInterface &
  PhotoBoothComponentInterface &
  PresentationPreviewComponentInterface &
  PresentationComponentInterface &
  QueueComponentInterface &
  SignupFormComponentInterface &
  SocialMediaComponentInterface &
  StageComponentInterface &
  VideoComponentInterface &
  UserProfileComponentInterface &
  SpeakersComponentInterface &
  ImageComponentInterface &
  QAComponentInterface &
  EventsListUiComponentInterface &
  TeamHeroMenuComponentInterface &
  ColumnComponentInterface &
  DividerComponentInterface;

export type ALL_COMPONENTS_UNION_TYPE =
  | AgendaComponentInterface
  | AppointmentComponentInterface
  | BoothLogosComponentInterface
  | BoothsComponentInterface
  | ChallengeComponentInterface
  | CountdownComponentInterface
  | CTAButtonComponentInterface
  | EventHomeComponentInterface
  | GalleryComponentInterface // deprecated and moved to image component
  | HeroMenuComponentInterface
  | HTMLComponentInterface
  | InfoBoothComponentInterface
  | MarginComponentInterface
  | MediaComponentInterface
  | NetworkComponentInterface
  | PhotoBoothComponentInterface
  | PresentationPreviewComponentInterface
  | PresentationComponentInterface
  | QueueComponentInterface
  | SignupFormComponentInterface
  | SocialMediaComponentInterface
  | StageComponentInterface
  | VideoComponentInterface
  | UserProfileComponentInterface
  | ImageComponentInterface
  | QAComponentInterface
  | EventsListUiComponentInterface
  | TeamHeroMenuComponentInterface
  | ColumnComponentInterface
  | DividerComponentInterface;

type socialMediasType = {
  [key: string]: string;
};
export const availableSocialMedias: socialMediasType = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  xtwitter: 'X/Twitter',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  website: 'Website',
};

export interface AgendaEntry {
  _id?: string;
  title: string;
  titleTranslations?: CustomTranslation[];
  description: string;
  descriptionTranslations?: CustomTranslation[];
  timestamp: string;
  url: string;
  speakers?: SpeakerEntry[];
  component?: string;
  additionalText?: string;
  image?: string;
  buttonTexts?: {
    linkText: string;
    detailsText: string;
  };
  titleColor?: string;
  descriptionColor?: string;
  // mbw only
  isTitle?: boolean;
  hideTime?: boolean;
}

export enum AgendaGroupBy {
  none = 'none',
  day = 'day',
  hour = 'hour',
}

export interface SpeakerEntry {
  _id?: string;
  image: string;
  name: string;
  nameTranslation: NestedValue<CustomTranslation[]>;
  position: string;
  positionTranslation: NestedValue<CustomTranslation[]>;
  description: string;
  descriptionTranslation: NestedValue<CustomTranslation[]>;
  url: string;
  linkName: string;
  hasModal: boolean;
  event: string;
}

// interface for the jsx elements of components
export interface UiComponentElementInterface<T extends UiComponent> {
  uiComponent?: T;
  height?: number;
}
export interface ComponentSettingsInterface<T> {
  onClose: () => void;
  onChange: (c: T) => void;
  component: T;
}

export interface ComponentStylingInterface<T> {
  onChange: (c: T) => void;
  component: T;
}
